import { AGE_BAND_TYPES } from '../../../helpers/constants';

// Hand-built Viator products are stored on the integration's settings JSON as
// `settings.viatorProducts`. Each product carries a readable SupplierProductCode
// (a slug of its name), a Viator-facing name, one internal ticket type (the
// source of seats / capacity) and a per-age-band map to one ticket option plus
// a custom net price in cents. Age bands left empty are simply not offered.

// Order the age-band rows are rendered in (adult, child, youth, infant, senior).
export const VIATOR_AGE_BANDS = Object.values(AGE_BAND_TYPES);

/**
 * Transient client-only id used as the React key for a not-yet-saved product
 * (whose code is only assigned on save). Stripped before persisting.
 */
export function generateClientId() {
    return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}${Math.random().toString(16).slice(2)}`;
}

/**
 * Turn a product name into a readable, code-safe slug: lowercase, accents
 * stripped, non-word runs collapsed to single underscores, trimmed, capped.
 */
export function slugifyProductName(name) {
    return (name || '')
        .toString()
        .normalize('NFKD')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 45);
}

/**
 * Assign a stable SupplierProductCode to every product that does not have one
 * yet, deriving it from the name and de-duplicating against codes already in
 * use (appending _2, _3, ...). Existing codes are preserved untouched - a code
 * must never change once Viator has mapped it. Also strips the transient
 * `_id` React key so it never reaches persisted settings.
 */
export function assignViatorProductCodes(products) {
    if (!Array.isArray(products)) {
        return products;
    }
    const used = new Set(products.map((product) => product.code).filter(Boolean));
    return products.map(({ _id, ...product }) => {
        if (product.code) {
            return product;
        }
        const base = slugifyProductName(product.name) || 'product';
        let code = base;
        let suffix = 2;
        while (used.has(code)) {
            code = `${base}_${suffix}`;
            suffix += 1;
        }
        used.add(code);
        return { ...product, code };
    });
}

/**
 * Validate the hand-built products before save. Returns the first problem as a
 * human-readable string, or null when everything is valid. The option dropdowns
 * already constrain options to the row's age band, so this only needs to catch
 * structural gaps (missing name / type / band / option / price and duplicate
 * names); the backend defends against stale mismatched references separately.
 */
export function validateViatorProducts(products) {
    if (!Array.isArray(products) || products.length === 0) {
        return null;
    }

    const seenNames = new Set();
    for (let i = 0; i < products.length; i += 1) {
        const product = products[i];
        const name = (product.name || '').trim();
        const label = name || `#${i + 1}`;

        if (!name) {
            return `Viator product ${i + 1}: name is required`;
        }
        const nameKey = name.toLowerCase();
        if (seenNames.has(nameKey)) {
            return `Duplicate Viator product name: "${name}"`;
        }
        seenNames.add(nameKey);

        if (!product.ticketTypeId) {
            return `Viator product "${label}": choose a ticket type`;
        }

        const ageBands = product.ageBands || {};
        const mappedBands = VIATOR_AGE_BANDS.filter((band) => ageBands[band]);
        if (mappedBands.length === 0) {
            return `Viator product "${label}": map at least one age band`;
        }

        for (const band of mappedBands) {
            const row = ageBands[band];
            if (!row.ticketOptionId) {
                return `Viator product "${label}" (${band}): choose an option`;
            }
            const price = Number(row.price);
            if (row.price == null || Number.isNaN(price) || price < 0) {
                return `Viator product "${label}" (${band}): enter a valid price`;
            }
        }
    }

    return null;
}
